import { SocialType, SocialTypeId } from "./Social.model";

/**
 * Detects the SocialTypeId from a URL by matching against the domain lists
 * provided by each SocialType (sourced from the backend via /api/social/types).
 *
 * Returns the matching SocialTypeId, WEBSITE as a fallback for any valid URL
 * that doesn't match a known platform, or null if the URL isn't parseable yet.
 */
export function detectSocialTypeIdFromUrl(
  url: string,
  availableTypes: SocialType[]
): SocialTypeId | null {
  if (!url || url.trim().length === 0) return null;

  // Normalise — add a protocol if missing so URL() doesn't throw
  let normalised = url.trim();
  if (!normalised.startsWith("http://") && !normalised.startsWith("https://")) {
    normalised = "https://" + normalised;
  }

  let hostname: string;
  try {
    hostname = new URL(normalised).hostname;
  } catch {
    // Not a valid URL yet — leave the selector as-is
    return null;
  }

  // Strip leading "www." so "www.linkedin.com" matches "linkedin.com"
  const bare = hostname.replace(/^www\./, "");

  for (const socialType of availableTypes) {
    if (socialType.domains.some((domain) => bare === domain || bare.endsWith("." + domain))) {
      return socialType.id;
    }
  }

  // Valid URL but no known platform matched
  return SocialTypeId.WEBSITE;
}

/**
 * Finds the full SocialType object matching a detected SocialTypeId,
 * falling back to OTHER if the detected id is not in the available list.
 */
export function resolveSocialType(
  detectedId: SocialTypeId,
  availableTypes: SocialType[]
): SocialType | null {
  return (
    availableTypes.find((t) => t.id === detectedId) ??
    availableTypes.find((t) => t.id === SocialTypeId.OTHER) ??
    null
  );
}
